import { Component, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { LoaderComponent } from '../../loader/loader.component';
import { MatButton } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogContent, MatDialogRef, MatDialogTitle } from '@angular/material/dialog';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MessageService } from '../../../services/message.service';
import { CustomFieldsService } from '../../../services/custom-fields.service';
import {
    ICustomFieldsConfig,
    ICustomFieldsConfigForm
} from '../../../typing/custom-fields-config.interface';
import { CustomFieldsConfig } from '../../../classes/custom-fields.class';
import { MatOption } from '@angular/material/autocomplete';
import { MatSelect } from '@angular/material/select';
import { EnumService } from '../../../services/enum.service';
import { CustomFieldTypeEnum } from '../../../enums/custom-field-type.enum';
import { MatCheckbox } from '@angular/material/checkbox';
import { CommonDropdownComponent } from '../../common-dropdawn/common-dropdown.component';
import { ProjectSelectorService } from '../../../services/project-selector.service';
import { CloseButtonComponent } from '../../close-button/close-button.component';
import { filter, switchMap } from 'rxjs';

@Component({
    selector: 'app-custom-fields-form',
    standalone: true,
    imports: [CommonModule, FormsModule, LoaderComponent, MatButton, MatDialogContent,
        MatFormField, MatInput, MatLabel, ReactiveFormsModule, MatOption, MatSelect, MatCheckbox, CommonDropdownComponent, CloseButtonComponent, MatDialogTitle],
    templateUrl: './custom-fields-form.component.html',
    styleUrl: './custom-fields-form.component.scss'
})
export class CustomFieldsFormComponent implements OnInit {
    readonly dialogRef = inject(MatDialogRef<CustomFieldsFormComponent>);
    readonly fb = inject(FormBuilder);
    readonly customFieldsService = inject(CustomFieldsService);
    protected enumService = inject(EnumService);
    public data: { customFieldsConfigId?: number } = inject(MAT_DIALOG_DATA);
    protected messagesService = inject(MessageService);
    protected projectSelectorService = inject(ProjectSelectorService);
    protected readonly CustomFieldTypeEnum = CustomFieldTypeEnum;
    loading = signal<boolean>(false);
    customFieldsConfig= new CustomFieldsConfig({} as ICustomFieldsConfig);

    customFieldsForm= this.fb.group({
        label: ['', Validators.required],
        type: ['', Validators.required],
        projectId: [0, Validators.required],
        required: [false],
        customId: [''],
    });

    ngOnInit(): void {
        if (this.data.customFieldsConfigId) {
            this.loading.set(true);
            this.customFieldsService.getCustomFieldConfig(this.data.customFieldsConfigId).subscribe( {
                next: data => {
                    this.customFieldsConfig = data;
                    this.customFieldsForm.patchValue(this.customFieldsConfig.formData());
                },
                error: error => {this.messagesService.errorMessage(error.message);}
            }).add(() => this.loading.set(false));
        }
    }

    onSubmit(): void {
        if (!this.customFieldsForm.valid || this.customFieldsForm.controls.projectId.value === 0) {
            return;
        }

        const request = this.data.customFieldsConfigId
            ? this.customFieldsService.updateCustomFieldConfig(
                this.customFieldsConfig.dataForUpdate(this.customFieldsForm.value as ICustomFieldsConfigForm))
            : this.customFieldsService.createCustomFieldConfig(
                this.customFieldsConfig.dataForCreate(this.customFieldsForm.value as ICustomFieldsConfigForm))
        ;

        request.subscribe({
            next: result => {
                this.messagesService.successMessage('Контакт успешно сохранен');
                this.dialogRef.close(result);},
            error: error => {this.messagesService.errorMessage(error.message);}
        });
    }

    deleteCustomField(id: number): void {
        this.messagesService.confirmMessage({
            title: 'Удаление настраиваемого поля',
            text: 'Вы действительно хотите удалить настраиваемое поле?',
            primaryText: 'Удалить',
            secondaryText: 'Отмена',
            showSecondaryButton: true,
        }).pipe(
            filter(result => result),
            switchMap(() => {
                return this.customFieldsService.deleteCustomFieldConfig([id]);
            }),
        ).subscribe({
            next: () => {
                this.messagesService.successMessage('Настраиваемое поле успешно удалено');
                this.dialogRef.close(true);
            },
            error: () => this.messagesService.errorMessage('При удалении произошла ошибка')
        });
    }
}
